// --- START OF FILE: src/components/HomePage.js ---
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  increment,
  serverTimestamp,
  collection,
  addDoc,
  runTransaction,
  collectionGroup,
  query,
  where,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Paper,
  Fade,
  Grow,
  Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import NoTagHomePage from './NoTagHomePage';
import AssignCardModal from './AssignCardModal';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3B82F6', 
      light: '#60A5FA',
      dark: '#2563EB',
    },
    secondary: {
      main: '#EC4899', 
      light: '#F472B6',
      dark: '#DB2777',
    },
    background: {
      default: '#0F172A',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#F8FAFC',
      secondary: '#CBD5E1',
    },
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.95rem',
          letterSpacing: '-0.01em',
          borderRadius: '12px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
          '@media (hover: none)': {
            '&:hover': {
              transform: 'none',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
          boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
            boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4), 0 10px 10px -5px rgba(59, 130, 246, 0.3)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: 'rgba(236, 72, 153, 0.5)',
          color: '#EC4899',
          backdropFilter: 'blur(10px)',
          background: 'rgba(236, 72, 153, 0.05)',
          '&:hover': {
            borderColor: '#EC4899',
            background: 'rgba(236, 72, 153, 0.1)',
            borderWidth: '2px',
          },
        },
      },
    },
  },
});

const useIntersectionObserver = (ref, options) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);
  
  return isIntersecting;
};

const LoadingScreen = () => (
  <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(ellipse at center, #1e3a8a 0%, #0f172a 100%)',
    position: 'relative',
    overflow: 'hidden',
  }}>
    <Box sx={{
      position: 'absolute',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '200%',
        height: '200%',
        top: '-50%',
        left: '-50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        animation: 'pulse 4s ease-in-out infinite',
      },
      '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
        '50%': { transform: 'scale(1.1)', opacity: 0.8 },
      },
    }} />
    
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <CircularProgress size={60} thickness={2} sx={{ color: '#3B82F6' }} />
        <Typography variant="body2" sx={{ mt: 3, color: '#CBD5E1', letterSpacing: '0.05em', fontWeight: 300 }}>
          Loading experience...
        </Typography>
      </Box>
    </motion.div>
  </Box>
);

const AnimatedBackground = () => (
  <Box sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
    background: 'radial-gradient(ellipse at top left, #1e3a8a 0%, #0f172a 50%, #0f172a 100%)',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '130%',
      height: '130%',
      top: '-15%',
      left: '-15%',
      background: 'radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.15) 0%, transparent 50%)',
      animation: 'float 20s ease-in-out infinite',
      '@media (max-width: 600px)': { animation: 'float 30s ease-in-out infinite' },
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      width: '120%',
      height: '120%',
      bottom: '-10%',
      right: '-10%',
      background: 'radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
      animation: 'float 20s ease-in-out infinite reverse',
      '@media (max-width: 600px)': { animation: 'float 30s ease-in-out infinite reverse' },
    },
    '@keyframes float': {
      '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
      '33%': { transform: 'translate(30px, -30px) rotate(120deg)' },
      '66%': { transform: 'translate(-20px, 20px) rotate(240deg)' },
    },
  }} />
);

const EnhancedButton = ({ children, onClick, variant, color, size, sx, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.5, type: "spring", stiffness: 100 }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
  >
    <Button
      variant={variant}
      color={color}
      size={size}
      onClick={onClick}
      sx={{
        ...sx,
        position: 'relative',
        overflow: 'hidden',
        '@media (max-width: 600px)': { minHeight: '48px', fontSize: '1rem' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 0,
          height: 0,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.2)',
          transform: 'translate(-50%, -50%)',
          transition: 'width 0.6s, height 0.6s',
        },
        '&:hover::before': { width: '300px', height: '300px' },
      }}
    >
      {children}
    </Button>
  </motion.div>
);

const useDynamicFontSize = (text, baseSize, minSize) => {
  const [fontSize, setFontSize] = useState(baseSize);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isMediumScreen = useMediaQuery('(max-width:900px)');
  const isExtraSmallScreen = useMediaQuery('(max-width:400px)');
  
  useEffect(() => {
    if (!text) return;
    const textLength = text.length;
    let calculatedSize = baseSize;
    if (isExtraSmallScreen) {
      if (textLength > 45) calculatedSize = Math.max(minSize * 0.85, baseSize * 0.55);
      else if (textLength > 35) calculatedSize = Math.max(minSize * 0.9, baseSize * 0.65);
      else calculatedSize = baseSize * 0.7;
    } else if (isSmallScreen) {
      if (textLength > 50) calculatedSize = Math.max(minSize, baseSize * 0.65);
      else if (textLength > 40) calculatedSize = Math.max(minSize, baseSize * 0.75);
      else calculatedSize = baseSize * 0.85;
    } else if (isMediumScreen) {
      if (textLength > 60) calculatedSize = Math.max(minSize, baseSize * 0.8);
      else if (textLength > 45) calculatedSize = Math.max(minSize, baseSize * 0.9);
    } else {
      if (textLength > 70) calculatedSize = Math.max(minSize, baseSize * 0.85);
      else if (textLength > 55) calculatedSize = Math.max(minSize, baseSize * 0.95);
    }
    setFontSize(calculatedSize);
  }, [text, baseSize, minSize, isSmallScreen, isMediumScreen, isExtraSmallScreen]);
  
  return fontSize;
};

const HomePage = () => {
  const [clientData, setClientData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [activePlatform, setActivePlatform] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showNoTagHomePage, setShowNoTagHomePage] = useState(false);
  const [errorMsgResponse, setErrorMsgResponse] = useState(null);
  const [showAssignCardModal, setShowAssignCardModal] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isExtraSmallScreen = useMediaQuery('(max-width:400px)');
  const contentRef = useRef(null);
  const isVisible = useIntersectionObserver(contentRef, { threshold: 0.1 });

  const queryParams = new URLSearchParams(location.search);
  const storeId = queryParams.get('storeId') || queryParams.get('store');
  const tagId = queryParams.get('tagId');
  const platformParam = queryParams.get('platform'); 

  const hitLogged = useRef(false);

  const titleText = activePlatform === 'Sonic' 
      ? 'How satisfied were you with your visit at Sonic overall?' 
      : `How was your experience with us today?`;
  
  const dynamicFontSize = useDynamicFontSize(titleText, 2, 1.1);

  const isLocalStorageAvailable = () => {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  };
  
  const handleSonicV2HitAndRedirect = async () => {
    if (!storeId) return;

    // Bot runs from the warehouse survey scraper carry ?bot=1 and must NOT be
    // counted as real QR scans. The warehouse records its own bot-hit count
    // (sonicV2Tracking.botHits); only genuine QR-code scans increment qrHits here.
    const isBotHit = queryParams.get('bot') === '1';

    if (!isBotHit) {
      try {
        const overallTrackingRef = doc(db, 'sonicV2Tracking', '_overall');
        const storeTrackingRef = doc(db, 'sonicV2Tracking', storeId);
        // Atomic server-side increments (no transaction: the previous version
        // interleaved a read after a write, which Firestore rejects, so every
        // hit silently failed). merge:true creates the doc on first hit.
        const writes = Promise.all([
          setDoc(overallTrackingRef, { qrHits: increment(1), lastQrHit: serverTimestamp() }, { merge: true }),
          setDoc(storeTrackingRef, { storeId: storeId, qrHits: increment(1), lastQrHit: serverTimestamp() }, { merge: true }),
        ]);
        // Cap the wait so a slow/offline network can never hang the redirect.
        await Promise.race([writes, new Promise((res) => setTimeout(res, 2500))]);
      } catch (error) {
        console.error('Error logging SonicV2NT QR hit:', error);
      }
    }

    const qualtricsBaseUrl = 'https://inspirebrands.qualtrics.com/jfe/form/SV_57lFt7uXYnODoxf';
    const fbclid = 'IwY2xjawKklZdleHRuA2FlbQIxMABicmlkETExcmxVcnJ3bDJ2dTJMcWNRAR7S9nLl9QYl8jxQ1BQ1lDGj-BLKmwbMgSUwAJ_HXAHwygIbZdYMcyinnXAbzA_aem_9sIiMXACQsn_q4ka0vGZag';
    const redirectUrl = `${qualtricsBaseUrl}?StoreID=${storeId}&fbclid=${fbclid}`;
    window.location.href = redirectUrl;
  };

  const logStoreHit = async (storeNum) => {
    try {
      const clientQuery = query(collection(db, 'clients'), where('clientMatch', '==', Number(storeNum)));
      let clientSnapshot = await getDocs(clientQuery);

      if (clientSnapshot.empty) {
        const clientQueryStr = query(collection(db, 'clients'), where('clientMatch', '==', String(storeNum)));
        clientSnapshot = await getDocs(clientQueryStr);
      }

      if (clientSnapshot.empty) {
        setErrorMsg(`Configuration not found for Store #${storeNum}`);
        setLoading(false);
        return;
      }

      const clientDoc = clientSnapshot.docs[0];
      const clientDataFetched = clientDoc.data();
      
      setClientId(clientDoc.id);
      setClientData(clientDataFetched);
      
      setActivePlatform('Google');

      const platformDocRef = doc(db, 'platforms', 'google');
      const platformDocSnap = await getDoc(platformDocRef);
      if (platformDocSnap.exists()) {
        setPlatformData(platformDocSnap.data());
      }

      const storageAvailable = isLocalStorageAvailable();
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      const hitKey = `hit-store-${storeNum}-${monthKey}`;

      if (storageAvailable && !localStorage.getItem(hitKey)) {
        await addDoc(collection(db, 'storeUsage'), {
          clientId: clientDoc.id,
          storeId: storeNum,
          action: 'hit_google_qr',
          timestamp: serverTimestamp(),
        });
        localStorage.setItem(hitKey, 'true');
      }

    } catch (error) {
       console.error('Error logging store hit:', error);
       setErrorMsg('An error occurred while loading the store data.');
    } finally {
       setLoading(false);
    }
  };

  useEffect(() => {
    if (hitLogged.current) return;

    if (storeId && platformParam?.toLowerCase() === 'google') {
      hitLogged.current = true;
      logStoreHit(storeId);
    } else if (storeId) {
      hitLogged.current = true;
      handleSonicV2HitAndRedirect();
    } else if (tagId) {
      hitLogged.current = true;
      logHit();
    } else {
      setLoading(false);
      setShowNoTagHomePage(true);
    }
  }, [storeId, tagId, platformParam]);

  const logHit = async () => {
    try {
      const nfcTagsQuery = query(collectionGroup(db, 'nfcTags'), where('tagId', '==', tagId));
      const querySnapshot = await getDocs(nfcTagsQuery);

      if (querySnapshot.empty) {
        setShowAssignCardModal(true);
        setLoading(false);
        return;
      }
      
      const nfcTagDoc = querySnapshot.docs[0];
      const tagData = nfcTagDoc.data();
      
      const clientDocRef = nfcTagDoc.ref.parent.parent;
      const clientIdValue = clientDocRef.id;
      setClientId(clientIdValue);

      const clientDocSnap = await getDoc(clientDocRef);
      if (!clientDocSnap.exists()) {
        setShowAssignCardModal(true);
        setLoading(false);
        return;
      }
      
      const clientDataFetched = clientDocSnap.data();
      setClientData(clientDataFetched);
      
      const resolvedPlatform = tagData.tagPlatform || tagData.reviewPlatform || clientDataFetched.reviewPlatform;
      setActivePlatform(resolvedPlatform);

      if (!clientDataFetched.assignedTo && clientDataFetched.displayName === '1aCardHolder') {
        setShowAssignCardModal(true);
      }

      if (resolvedPlatform) {
        const platformDocRef = doc(db, 'platforms', resolvedPlatform.toLowerCase());
        const platformDocSnap = await getDoc(platformDocRef);
        if (platformDocSnap.exists()) {
          setPlatformData(platformDocSnap.data());
        }
      }

      const storageAvailable = isLocalStorageAvailable();
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      const hitKey = `hit-${tagId}-${monthKey}`;

      if (storageAvailable && !localStorage.getItem(hitKey)) {
        await runTransaction(db, async (transaction) => {
          transaction.update(nfcTagDoc.ref, { totalHits: increment(1), lastHit: serverTimestamp() });
          const tagUsageDocRef = doc(collection(db, 'tagUsage'));
          transaction.set(tagUsageDocRef, {
            clientId: clientIdValue,
            nfcTagId: tagId,
            action: 'hit',
            timestamp: serverTimestamp(),
          });
        });
        localStorage.setItem(hitKey, 'true');
      }
    } catch (error) {
      console.error('Error logging hit:', error);
      setErrorMsg('An error occurred while logging your visit.');
    } finally {
      setLoading(false);
    }
  };

  const generateTokenId = () => {
    const timestamp = Date.now().toString(36);
    const randomNum = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${randomNum}`;
  };

  const generateAndStoreToken = async (clientId) => {
    const newTokenId = generateTokenId();
    const tokenDocRef = doc(collection(db, 'tokens'), newTokenId);
    await setDoc(tokenDocRef, { tokenId: newTokenId, clientId: clientId, used: false, timestamp: new Date() });
    try { localStorage.setItem('accessToken', newTokenId); } catch (e) {}
  };

  const handleToken = async (clientId) => {
    if (!clientId) throw new Error('Client ID is missing.');
    if (!isLocalStorageAvailable()) return;
    
    let existingToken = localStorage.getItem('accessToken');
    if (existingToken) {
      const existingTokenDocRef = doc(db, 'tokens', existingToken);
      const existingTokenSnap = await getDoc(existingTokenDocRef);
      if (!existingTokenSnap.exists()) await generateAndStoreToken(clientId);
    } else {
      await generateAndStoreToken(clientId);
    }
  };

  const handleResponse = async (responseType, responseDetail) => {
    if (!clientData || !clientId) return;

    try {
      let redirectUrl;
      const isSonic = activePlatform === 'Sonic';
      
      if (isSonic) {
        const sonicStoreId = clientData.clientMatch;
        if (!sonicStoreId) {
          setErrorMsgResponse("Sorry, there's a configuration issue. The Store ID is missing.");
          return;
        }
        const qualtricsBaseUrl = 'https://inspirebrands.qualtrics.com/jfe/form/SV_57lFt7uXYnODoxf';
        const fbclid = 'IwY2xjawKklZdleHRuA2FlbQIxMABicmlkETExcmxVcnJ3bDJ2dTJMcWNRAR7S9nLl9QYl8jxQ1BQ1lDGj-BLKmwbMgSUwAJ_HXAHwygIbZdYMcyinnXAbzA_aem_9sIiMXACQsn_q4ka0vGZag';
        redirectUrl = `${qualtricsBaseUrl}?StoreID=${sonicStoreId}&fbclid=${fbclid}`;
      } else {
        // ---------------------------------------------------------
        // ARCHITECTURAL FIX 2: Check for googlePlaceId first!
        // This restores the perfect auto-popup review modal behavior.
        // ---------------------------------------------------------
        if (clientData.googlePlaceId) {
            redirectUrl = `https://search.google.com/local/writereview?placeid=${clientData.googlePlaceId}`;
        } else if (clientData.googleReviewUrl) {
            redirectUrl = clientData.googleReviewUrl;
        } else if (platformData?.baseUrl) {
            redirectUrl = `${platformData.baseUrl}${clientData.platformId || ''}`;
        }
      }

      const storageAvailable = isLocalStorageAvailable();
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      
      const storageId = tagId ? `tag-${tagId}` : `store-${storeId}`;
      const yesKey = `yes-${storageId}-${monthKey}`;
      const noKey = `no-${storageId}-${monthKey}`;
      
      let assignedToField = null;
      let placementField = null;
      
      if (tagId) {
        const rootNfcTagDocRef = doc(db, 'nfcTags', tagId);
        const rootNfcTagSnap = await getDoc(rootNfcTagDocRef);
        assignedToField = rootNfcTagSnap.exists() ? rootNfcTagSnap.data().assignedTo : null;
        placementField = rootNfcTagSnap.exists() ? rootNfcTagSnap.data().placement : null;
      }
      
      const displayNameField = clientData.displayName || null;
      const hasRespondedThisMonth = (responseType === 'yes' && localStorage.getItem(yesKey)) || (responseType === 'no' && localStorage.getItem(noKey));

      if (storageAvailable && !hasRespondedThisMonth) {
        const responseData = {
          responseType,
          responseDetail,
          timestamp: serverTimestamp(),
          clientId,
          assignedTo: assignedToField,
          placement: placementField,
          displayName: displayNameField,
          platformUsed: activePlatform
        };

        if (tagId) {
            const nfcTagDocRef = doc(db, 'clients', clientId, 'nfcTags', tagId);
            await setDoc(nfcTagDocRef, { [responseType === 'yes' ? 'totalYes' : 'totalNo']: increment(1) }, { merge: true });
            await addDoc(collection(db, 'clients', clientId, 'nfcTags', tagId, 'responses'), responseData);
            await addDoc(collection(db, 'tagUsage'), {
                clientId,
                nfcTagId: tagId,
                action: responseType,
                timestamp: serverTimestamp(),
            });
        } else {
            await addDoc(collection(db, 'clients', clientId, 'responses'), responseData);
            await addDoc(collection(db, 'storeUsage'), {
                clientId,
                storeId: storeId,
                action: responseType,
                timestamp: serverTimestamp(),
            });
        }

        localStorage.setItem(responseType === 'yes' ? yesKey : noKey, 'true');
      }

      await handleToken(clientId);
      
      if (isSonic) {
        window.location.href = redirectUrl;
      } else if (responseType === 'yes') {
        navigate('/claim-reward', { 
          state: { 
            clientId, 
            tagId: tagId || null, 
            redirectUrl,
            assignedTo: assignedToField
          } 
        });
      } else {
        navigate('/apology', { 
          state: { 
            apologyMessage: clientData.apologyMessage, 
            clientId, 
            tagId: tagId || null 
          } 
        });
      }
    } catch (error) {
      console.error('Error handling response:', error);
      setErrorMsgResponse('An error occurred while processing your response.');
    }
  };

  const handleAssignModalSubmit = async (codes) => {
    if (!tagId) return;
    const { parentCode, businessCode, assignedTo, placement } = codes;
    try {
      const parentDocRef = doc(db, 'parentCodeMap', parentCode);
      const parentDocSnap = await getDoc(parentDocRef);
      if (!parentDocSnap.exists()) throw new Error("Parent code not found");
      
      const { childMap: childMapValue } = parentDocSnap.data();
      const clientQuery = query(collection(db, 'clients'), where('childMap', '==', childMapValue), where('clientMatch', '==', Number(businessCode)));
      const clientQuerySnapshot = await getDocs(clientQuery);
      if (clientQuerySnapshot.empty) throw new Error("No matching client found");
      
      const clientDoc = clientQuerySnapshot.docs[0];
      const cardData = {
        assignedAt: serverTimestamp(),
        assignedTo: assignedTo,
        clientId: clientDoc.id,
        id: tagId,
        placement: placement,
        tagId: tagId,
      };
      await setDoc(doc(db, 'clients', clientDoc.id, 'nfcTags', tagId), cardData);
      
      setTimeout(() => {
        setShowAssignCardModal(false);
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error in assignment submission:", error);
    }
  };

  if (loading) return <LoadingScreen />;
  if (showNoTagHomePage) return <NoTagHomePage />;
  if (errorMsg) return <Box sx={{ minHeight: '100vh', background: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Alert severity="error">{errorMsg}</Alert></Box>;
  if (errorMsgResponse) return <Box sx={{ minHeight: '100vh', background: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Alert severity="error">{errorMsgResponse}</Alert></Box>;
  if (showAssignCardModal) return <AssignCardModal open={showAssignCardModal} handleClose={() => setShowAssignCardModal(false)} handleSubmit={handleAssignModalSubmit} />;

  const isSonic = activePlatform === 'Sonic';

  const sonicOptions = [
    { emoji: '😡', text: 'Very Dissatisfied', value: 'Very Dissatisfied', color: '#EF4444' },
    { emoji: '☹️', text: 'Dissatisfied', value: 'Dissatisfied', color: '#F97316' },
    { emoji: '😐', text: 'Neither', value: 'Neither Satisfied nor Dissatisfied', color: '#EAB308' },
    { emoji: '🙂', text: 'Satisfied', value: 'Satisfied', color: '#22C55E' },
    { emoji: '😃', text: 'Very Satisfied', value: 'Very Satisfied', color: '#3B82F6', isPrimary: true },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', position: 'relative', overflowX: 'hidden' }}>
        <AnimatedBackground />
        <Container 
          ref={contentRef}
          sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, padding: { xs: '16px', sm: '24px' } }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{ width: '100%', maxWidth: '800px' }}
            >
              <Paper
                elevation={0}
                sx={{ padding: { xs: '24px 16px', sm: '40px 30px' }, borderRadius: '24px', backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
              >
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }} transition={{ duration: 0.6, delay: 0.2 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontSize: `${dynamicFontSize}rem`, mb: 4, background: 'linear-gradient(135deg, #F8FAFC 0%, #CBD5E1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {titleText}
                  </Typography>
                </motion.div>

                {isSonic ? (
                  <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    {sonicOptions.map((option, index) => (
                      <EnhancedButton
                        key={option.value}
                        onClick={() => handleResponse(option.isPrimary ? 'yes' : 'no', option.value)}
                        variant={option.isPrimary ? 'contained' : 'outlined'}
                        color={option.isPrimary ? 'primary' : 'secondary'}
                        delay={index * 0.1}
                        sx={{ maxWidth: '400px', py: 2, ...(option.isPrimary && { background: `linear-gradient(135deg, ${option.color} 0%, ${option.color}CC 100%)` }), ...(!option.isPrimary && { borderColor: `${option.color}80`, color: option.color }) }}
                      >
                        <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>{option.emoji}</span>
                        {option.text}
                      </EnhancedButton>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <EnhancedButton
                      onClick={() => handleResponse('yes', 'Yes')}
                      variant="contained"
                      color="primary"
                      delay={0.2}
                      sx={{ width: { xs: '100%', sm: '200px' }, py: 1.8 }}
                    >
                      Great Experience!
                    </EnhancedButton>
                    <EnhancedButton
                      onClick={() => handleResponse('no', 'No')}
                      variant="outlined"
                      color="secondary"
                      delay={0.3}
                      sx={{ width: { xs: '100%', sm: '200px' }, py: 1.8 }}
                    >
                      Could be better
                    </EnhancedButton>
                  </Box>
                )}
              </Paper>
            </motion.div>
          </AnimatePresence>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default HomePage;
// --- END OF FILE: src/components/HomePage.js ---